/**
 * Canonical URL-derived scope (W1 S2a).
 *
 * Before this, "which environment/portal am I looking at?" lived in three
 * places that could disagree: EnvironmentContext's own component state
 * (written to the URL as a raw side-channel history write that no popstate
 * listener ever read back), ModelConfigPortalContext's `?portal=`
 * (deliberately separate — Bedrock model-config portal choice, not the
 * env/estate scope), and a handful of pages reading `?env=` off
 * `window.location` directly (e.g. Monitor.tsx).
 *
 * ScopeContext replaces the first of those with a single REACTIVE source:
 * scope is *derived* from the router's current location on every render, and
 * a scope change is a real router navigation (a merge-preserving push — the
 * existing query string is kept, only the changed keys are touched) rather
 * than a side-channel URL write. That is what makes Back and reload behave:
 * the browser's own history stack now carries the scope, so popping it is a
 * real navigation the router sees and re-renders from.
 *
 * Two query keys carry the environment, kept deliberately in sync:
 *  - `environmentId` is the canonical W1 key — it is what the launch pack's
 *    own Screen Contract sheet specifies for every new route, e.g. H01's
 *    canonical route is `/home?view=attention[&environmentId=...&portalId=...]`.
 *  - `env` is the pre-existing key EnvironmentContext has always used, and
 *    which Monitor.tsx and any already-shared/bookmarked link reads directly
 *    off `window.location` (never through this context). Dropping it would
 *    silently break those.
 * Reading prefers `environmentId`, falling back to `env`; writing sets both,
 * so an old `?env=` link and a new `?environmentId=` link both work, and a
 * scope change updates both at once rather than making one go stale.
 *
 * `useRouteScope()` is the context-free core: it needs only a Router
 * ancestor, not a `<ScopeProvider>`. EnvironmentContext's adapter calls it
 * directly for exactly this reason — hub-pages-smoke and env-pages-smoke
 * mount `EnvironmentProvider` straight under a bare `MemoryRouter` with no
 * `ScopeProvider` in between, and must keep passing unmodified.
 */
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/** Canonical W1 query key for the environment scope field (per the launch pack's Screen Contract canonical routes). */
export const ENVIRONMENT_ID_PARAM = 'environmentId';
/** Legacy query key for the same field — kept in sync for backward compatibility (Monitor.tsx, EnvironmentContext's historical public contract). */
export const LEGACY_ENV_PARAM = 'env';
/** Query key for the portal scope field (new in W1 — no legacy key to preserve). */
export const PORTAL_PARAM = 'portalId';

export interface Scope {
  /** null = no environment in scope ("all" — read-only per the docx rule). */
  environmentId: string | null;
  /** null = no portal in scope. */
  portalId: string | null;
}

export interface ScopeContextValue extends Scope {
  /** Merge-preserving router push: only the given keys change, everything else in the query string survives. Pass null to clear a key. */
  setScope: (patch: Partial<Scope>) => void;
  setEnvironmentId: (environmentId: string | null) => void;
  setPortalId: (portalId: string | null) => void;
}

function deriveScope(searchParams: URLSearchParams): Scope {
  return {
    environmentId: searchParams.get(ENVIRONMENT_ID_PARAM) ?? searchParams.get(LEGACY_ENV_PARAM),
    portalId: searchParams.get(PORTAL_PARAM),
  };
}

/**
 * Context-free scope source. Derives {environmentId, portalId} from the
 * current location on every render and exposes a merge-preserving,
 * idempotent setter. Requires only a Router ancestor (BrowserRouter or
 * MemoryRouter) — no ScopeProvider needed.
 */
export function useRouteScope(): ScopeContextValue {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const scope = useMemo(() => deriveScope(searchParams), [searchParams]);

  const setScope = useCallback((patch: Partial<Scope>) => {
    // Idempotent by VALUE, not by raw query string: a no-op patch (every
    // patched field already equals the current derived scope) must not push
    // a new history entry, or Back would have to be pressed twice to undo
    // one real scope change (and route-param-driven effects that call this
    // on every render — see EnvironmentDetail.tsx — would loop). Checking
    // derived values rather than the raw string also correctly treats
    // filling in a missing legacy `env=` alongside an already-correct
    // `environmentId=` as a real (if minor) URL change, not a semantic one.
    const patchedKeys = Object.keys(patch) as (keyof Scope)[];
    const unchanged = patchedKeys.every((key) => patch[key] === scope[key]);
    if (unchanged) return;

    const next = new URLSearchParams(location.search);
    if ('environmentId' in patch) {
      const value = patch.environmentId;
      if (value === null || value === undefined) {
        next.delete(ENVIRONMENT_ID_PARAM);
        next.delete(LEGACY_ENV_PARAM);
      } else {
        next.set(ENVIRONMENT_ID_PARAM, value);
        next.set(LEGACY_ENV_PARAM, value);
      }
    }
    if ('portalId' in patch) {
      const value = patch.portalId;
      if (value === null || value === undefined) next.delete(PORTAL_PARAM);
      else next.set(PORTAL_PARAM, value);
    }
    const search = next.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [location.pathname, location.search, navigate, scope]);

  const setEnvironmentId = useCallback(
    (environmentId: string | null) => setScope({ environmentId }),
    [setScope],
  );
  const setPortalId = useCallback(
    (portalId: string | null) => setScope({ portalId }),
    [setScope],
  );

  return { ...scope, setScope, setEnvironmentId, setPortalId };
}

const ScopeContext = createContext<ScopeContextValue | undefined>(undefined);

export const ScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useRouteScope();
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

/**
 * Reads scope from the nearest ScopeProvider when one is mounted; otherwise
 * derives it directly from the router (see useRouteScope). This graceful
 * fallback — rather than throwing when no Provider is present — is what lets
 * EnvironmentContext's adapter and any other early consumer work correctly
 * before ScopeProvider is universally mounted everywhere.
 */
export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeContext);
  // Always called (never conditionally) so hook order stays stable regardless
  // of whether a ScopeProvider ancestor exists.
  const fallback = useRouteScope();
  return ctx ?? fallback;
}
